import React, { memo } from 'react';
import {
  Box,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material';
import { QuestionType, QuestionImportance, SearchScope } from './types';
import FilterBar from '../common/FilterBar';
import SearchableSelect from '../common/SearchableSelect';
import { STICKY_TOP, TOP_BAR } from './ChapterSidebar';

interface QuestionFiltersProps {
  chapterId: string;
  searchQuery: string;
  filterType: string;
  filterImportance: string;
  /** A search is on its way back. */
  searching: boolean;
  searchScope: SearchScope;
  onSearchScopeChange: (scope: SearchScope) => void;
  onSearchChange: (chapterId: string, value: string) => void;
  onTypeChange: (chapterId: string, value: string) => void;
  onImportanceChange: (chapterId: string, value: string) => void;
  onClearFilters: (chapterId: string) => void;
}

const TYPE_OPTIONS = Object.values(QuestionType).map((v) => ({ value: v, label: v }));
const IMPORTANCE_OPTIONS = Object.values(QuestionImportance).map((v) => ({ value: v, label: v }));

const scopeSx = {
  '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', px: 1.25, py: 0.25, borderRadius: '8px' },
} as const;

const QuestionFilters = memo<QuestionFiltersProps>(({
  chapterId,
  searchQuery,
  filterType,
  filterImportance,
  searching,
  searchScope,
  onSearchScopeChange,
  onSearchChange,
  onTypeChange,
  onImportanceChange,
  onClearFilters,
}) => {
  const hasFilters = searchQuery || filterType || filterImportance;
  const hasQuery = !!searchQuery.trim();

  return (
    // Pinned level with the chapter list, so search and filters stay at hand
    // while scrolling. The strip of page colour above the bar keeps questions
    // from showing between it and the top bar.
    <Box
      sx={{
        position: { md: 'sticky' },
        top: TOP_BAR,
        zIndex: 2,
        pt: { md: `${STICKY_TOP - TOP_BAR}px` },
        mt: { md: `-${STICKY_TOP - TOP_BAR}px` },
        mb: 2,
        bgcolor: 'background.default',
      }}
    >
      <FilterBar sx={{ mb: 0 }}>
        <TextField
          size="small"
          placeholder="Search questions, options, tags or years"
          value={searchQuery}
          onChange={(e) => onSearchChange(chapterId, e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Escape' && searchQuery) onSearchChange(chapterId, ''); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: (searching || searchQuery) ? (
              <InputAdornment position="end">
                {searching ? (
                  <CircularProgress size={16} thickness={5} />
                ) : (
                  <IconButton size="small" aria-label="Clear search" onClick={() => onSearchChange(chapterId, '')} edge="end">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </InputAdornment>
            ) : undefined,
          }}
          sx={{ flex: 1, minWidth: 250 }}
        />
        <SearchableSelect
          label="Type"
          placeholder="All types"
          value={filterType}
          onChange={(v) => onTypeChange(chapterId, v)}
          options={TYPE_OPTIONS}
          minWidth={160}
        />
        <SearchableSelect
          label="Importance"
          placeholder="All"
          value={filterImportance}
          onChange={(v) => onImportanceChange(chapterId, v)}
          options={IMPORTANCE_OPTIONS}
          minWidth={150}
        />
        {hasFilters && (
          <Button size="small" onClick={() => onClearFilters(chapterId)} sx={{ textTransform: 'none' }}>
            Clear all
          </Button>
        )}

        {/* Where the search looks — only meaningful while there is a query */}
        {hasQuery && (
          <Box sx={{ flexBasis: '100%' }}>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={searchScope}
              onChange={(_, v: SearchScope | null) => v && onSearchScopeChange(v)}
              sx={{ ...scopeSx, gap: 0.5, '& .MuiToggleButtonGroup-grouped': { border: 0 } }}
            >
              <ToggleButton value="chapter">In this chapter</ToggleButton>
              <ToggleButton value="bank">All chapters</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}
      </FilterBar>
    </Box>
  );
});

QuestionFilters.displayName = 'QuestionFilters';

export default QuestionFilters;
